import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import HeartbeatMonitor from './HeartbeatMonitor';

function DistributeurDetailModal({ distributeur, onClose, onRestart, onDisable, logs }) {
  const [commands, setCommands] = useState([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(distributeur?.display_name || distributeur?.id || '');

  // Load command history
  useEffect(() => {
    if (!distributeur) return;
    const loadCommands = async () => {
      const { data, error } = await supabase
        .from('commands')
        .select('*')
        .eq('distributeur_id', distributeur.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setCommands(data);
      }
    };

    loadCommands();

    // Subscribe to new commands
    const channel = supabase
      .channel(`commands-modal-${distributeur.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'commands',
        filter: `distributeur_id=eq.${distributeur.id}`
      }, () => {
        loadCommands();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [distributeur]);

  if (!distributeur) return null;

  // Filter logs for this specific distributeur
  const distributeurLogs = logs.filter(log => log.includes(distributeur.id));

  const handleRename = async () => {
    if (!newName.trim()) {
      alert('Le nom ne peut pas être vide');
      return;
    }

    const { error } = await supabase
      .from('distributeurs')
      .update({ display_name: newName.trim() })
      .eq('id', distributeur.id);

    if (error) {
      alert(`Erreur: ${error.message}`);
    } else {
      setIsRenaming(false);
      // Update local state
      distributeur.display_name = newName.trim();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'executing': return '#3b82f6';
      case 'failed': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return '✓ Complété';
      case 'executing': return '⟳ En cours';
      case 'failed': return '✗ Échoué';
      default: return '⋯ En attente';
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "2rem",
          maxWidth: "800px",
          width: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div style={{ flex: 1 }}>
            {!isRenaming ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h2 style={{ margin: 0 }}>{distributeur.display_name || distributeur.id}</h2>
                <button
                  onClick={() => setIsRenaming(true)}
                  style={{
                    background: "transparent",
                    border: "1px solid #ddd",
                    padding: "0.25rem 0.5rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    color: "#666"
                  }}
                >
                  ✏️ Renommer
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{
                    padding: "0.5rem",
                    fontSize: "1rem",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    flex: 1
                  }}
                  autoFocus
                />
                <button
                  onClick={handleRename}
                  style={{
                    background: "#22c55e",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.85rem"
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setIsRenaming(false);
                    setNewName(distributeur.display_name || distributeur.id);
                  }}
                  style={{
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.85rem"
                  }}
                >
                  ✗
                </button>
              </div>
            )}
            {distributeur.display_name && distributeur.display_name !== distributeur.id && (
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.8rem", color: "#999" }}>
                ID: {distributeur.id}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ✕
          </button>
        </div>

        {/* Heartbeat Status */}
        <div style={{ marginBottom: "1.5rem", padding: "1rem", background: "#f8fafc", borderRadius: "8px" }}>
          <HeartbeatMonitor distributeurId={distributeur.id} />
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" }}>
          <button
            onClick={() => onRestart(distributeur.id)}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "500",
            }}
          >
            🔄 Redémarrer
          </button>
          <button
            onClick={() => onDisable(distributeur.id)}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "500",
            }}
          >
            ⏸️ Désactiver
          </button>
          <button
            onClick={() => {
              const hostname = distributeur.tailscale_host || distributeur.id;
              const username = distributeur.ssh_username || "pi";
              const password = distributeur.ssh_password || "kiwo12";
              const sshCommand = `ssh ${username}@${hostname}`;

              navigator.clipboard.writeText(sshCommand).then(() => {
                alert(`📋 Commande SSH copiée:\n${sshCommand}\n\nMot de passe: ${password}\n\nCollez la commande dans votre terminal, puis entrez le mot de passe quand demandé.`);
              }).catch(() => {
                alert(`Commande SSH: ${sshCommand}\nMot de passe: ${password}`);
              });
            }}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "500",
            }}
          >
            🔌 Connecter SSH
          </button>
        </div>

        {/* Drinks Status */}
        <div style={{ marginBottom: "2rem" }}>
          <h3>État des boissons</h3>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            {(distributeur.drinks || []).length === 0 ? (
              <p style={{ color: "#999" }}>Aucune boisson enregistrée</p>
            ) : (
              distributeur.drinks.map((drink, i) => {
                const percent = Math.round((drink.curr_capacity / drink.max_capacity) * 100);
                return (
                  <div
                    key={i}
                    style={{
                      padding: "1rem",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      minWidth: "150px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <h4 style={{ margin: "0 0 0.5rem 0" }}>{drink.drink_name}</h4>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <div
                        style={{
                          width: "100%",
                          height: "20px",
                          backgroundColor: "#eee",
                          borderRadius: "10px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${percent}%`,
                            height: "100%",
                            backgroundColor:
                              percent < 25 ? "#e53935" : percent < 60 ? "#fb8c00" : "#43a047",
                            transition: "width 0.3s ease",
                          }}
                        ></div>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: "0.9rem", color: "#333" }}>
                      <strong>{percent}%</strong> - {drink.curr_capacity} / {drink.max_capacity} ml
                    </p>
                    <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem", color: "#666" }}>
                      ID: {drink.drink_id}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Command History */}
        <div style={{ marginBottom: "2rem" }}>
          <h3>Historique des commandes</h3>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "1rem",
              maxHeight: "300px",
              overflowY: "auto",
              backgroundColor: "#f8fafc",
            }}
          >
            {commands.length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>Aucune commande envoyée</p>
            ) : (
              commands.map((cmd) => (
                <div
                  key={cmd.id}
                  style={{
                    padding: "0.75rem",
                    marginBottom: "0.5rem",
                    borderRadius: "6px",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: "0.9rem" }}>{cmd.command_type}</strong>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          padding: "2px 8px",
                          borderRadius: 12,
                          background: getStatusColor(cmd.status) + '20',
                          color: getStatusColor(cmd.status),
                          fontWeight: 600
                        }}
                      >
                        {getStatusText(cmd.status)}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#666" }}>
                      {new Date(cmd.created_at).toLocaleString()}
                      {cmd.completed_at && (
                        <span> • Complété: {new Date(cmd.completed_at).toLocaleString()}</span>
                      )}
                    </div>
                    {cmd.error_message && (
                      <div style={{ fontSize: "0.75rem", color: "#ef4444", marginTop: "0.25rem" }}>
                        Erreur: {cmd.error_message}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Logs */}
        <div>
          <h3>Logs du distributeur</h3>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "1rem",
              maxHeight: "300px",
              overflowY: "auto",
              backgroundColor: "#f5f5f5",
              fontFamily: "monospace",
              fontSize: "0.85rem",
            }}
          >
            {distributeurLogs.length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>Aucun log disponible</p>
            ) : (
              distributeurLogs.map((log, i) => (
                <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid #e0e0e0" }}>
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DistributeurDetailModal;
